import type { FC } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Link as RouterLink, useSearchParams } from "react-router";
import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import { useMutation, type MutationStatus } from "@tanstack/react-query";
import { DownloadIcon } from "../../shared/icons";
import { downloadPDFReportsMutationOptions } from "../../api/reports";
import { downloadBlob } from "../../shared/downloadBlob";
import { PageWrapper } from "../../shared/Page";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";

const TEXTS: Record<MutationStatus, { title: TranslationKey; line: TranslationKey }> = {
  idle: { title: "DownloadPage.YourPDFIsReady", line: "DownloadPage.DownloadMessage" },
  pending: { title: "DownloadPage.YourPDFIsReady", line: "DownloadPage.DownloadMessage" },
  success: { title: "DownloadPage.DownloadSuccessful", line: "DownloadPage.DownloadSuccessMessage" },
  error: { title: "DownloadPage.FileNotFound", line: "DownloadPage.FileNotFoundMessage" },
};

export const DownloadReportsPage: FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const filename = searchParams.get("filename");
  const downloadPDFReportsMutation = useMutation(downloadPDFReportsMutationOptions());
  const { paths } = usePrivacyNavigate();
  const { status } = downloadPDFReportsMutation;
  const texts = TEXTS[status];

  const handleDownload = () => {
    downloadPDFReportsMutation.mutate(token, {
      onSuccess: response => downloadBlob(response, filename),
    });
  };

  return (
    <PageWrapper sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <Stack direction="column" spacing={4} sx={{ maxWidth: 500, mt: "-30%" }}>
        <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
          <Typography variant="h1" sx={{ fontSize: theme => theme.typography.pxToRem(24) }}>
            {t(texts.title)}
          </Typography>
          <Typography sx={theme => ({ color: theme.palette.text.secondary, fontSize: theme.typography.pxToRem(14) })}>
            {t(texts.line)}
          </Typography>
        </Box>
        {status === "error" && (
          <Box>
            <Button
              sx={{ minWidth: 250 }}
              size="medium"
              variant="outlined"
              component={RouterLink}
              to={paths.registrationsGrid({ variant: "Controller" })}
            >
              {t("DownloadPage.GoToProcessings")}
            </Button>
          </Box>
        )}
        {(status === "idle" || status === "pending") && (
          <Box>
            <Button
              sx={{ minWidth: 250 }}
              size="medium"
              loading={status === "pending"}
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
            >
              {t("DownloadPage.DownloadPDFFile")}
            </Button>
          </Box>
        )}
      </Stack>
    </PageWrapper>
  );
};
